<template>
    <div class="table-rounded overflow-hidden">
        <div ref="scrollContainer" class="table-responsive table-sticky-header" style="max-height: 60vw; overflow: auto">
            <table :class="{ 'table-empty': !items.length }" class="table-rounded table-no-outline table-striped table-hover mb-0 table border">
                <thead>
                    <tr style="border-top: none">
                        <th style="vertical-align: middle; overflow: hidden">
                            <div class="text-truncate flex items-center">
                                {{ trans('workorder.overview.records.cell.code') }}
                            </div>
                        </th>
                        <th style="vertical-align: middle; overflow: hidden">
                            <div class="text-truncate flex items-center">
                                {{ trans('workorder.overview.records.cell.title') }}
                            </div>
                        </th>
                        <th style="vertical-align: middle; overflow: hidden">
                            <div class="text-truncate flex items-center">
                                {{ trans('workorder.overview.records.cell.assignee') }}
                            </div>
                        </th>
                        <th style="vertical-align: middle; overflow: hidden">
                            <div class="text-truncate flex items-center">
                                {{ trans('workorder.overview.records.cell.processing_time') }}
                            </div>
                        </th>
                        <th style="vertical-align: middle; overflow: hidden">
                            <div class="flex items-center">
                                {{ trans('workorder.overview.records.cell.result') }}
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody v-if="items.length">
                    <tr v-for="item in items" :key="item.id" class="cursor-pointer" @click.stop="loadWorkOrderDetails(item.id)">
                        <td>
                            <div class="text-nowrap">{{ item.code }}</div>
                        </td>
                        <td>
                            <div class="align-items-start flex" style="gap: 5px">
                                <div>
                                    <work-order-type-icon :work-order="item" style="height: 22px" />
                                </div>
                                <div class="line-clamp-2">
                                    {{ item.title }}
                                </div>
                            </div>
                        </td>
                        <td>
                            <div class="text-truncate flex items-center">
                                {{ item.assignee?.name || '-' }}
                            </div>
                        </td>
                        <td>
                            <div v-if="item.processingTimeInSeconds">
                                {{ $filters.duration(item.processingTimeInSeconds, currentLocale) }}
                            </div>
                            <div v-else>-</div>
                        </td>
                        <td>
                            <div class="flex items-center gap-4">
                                <icon v-if="item.status.value === 'resolved'" icon="cil-check-alt" size="lg" />
                                <icon v-if="item.status.value === 'closed'" icon="cil-x" size="lg" />
                                <badge v-if="item.resolution" color="warning">{{ item.resolution.label }}</badge>
                                <div class="text-bold text-nowrap">
                                    {{ $filters.fullDate(item.statusChangedAt, currentLocale) }}
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
                <tbody v-else>
                    <tr>
                        <td class="td-empty" colspan="5">
                            <div class="flex items-center justify-center py-5">
                                <b>{{ trans('workorder.overview.records.empty') }}</b>
                            </div>
                        </td>
                    </tr>
                </tbody>
                <tfoot v-if="isLoading">
                    <tr>
                        <td colspan="5">
                            <div class="flex items-center justify-center">
                                <spinner />
                            </div>
                        </td>
                    </tr>
                </tfoot>
            </table>
            <div ref="landmark" class="h-5" />
        </div>
    </div>
</template>
<script setup>
import Badge from '@/Components/Badge.vue';
import WorkOrderTypeIcon from '@/Pages/WorkOrders/Modals/WorkOrderTypeIcon.vue';
import { loadWorkOrderDetails } from '@/Pages/WorkOrders/loadWorkOrderDetails';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useWorkOrderStore } from '@/stores/workOrders';

const workOrderStore = useWorkOrderStore();
const scrollContainer = ref(null);
const landmark = ref(null);
const observer = ref(null);

const items = computed(() => workOrderStore.archived.items ?? []);
const isLoading = computed(() => workOrderStore.archived.isLoading);

onBeforeUnmount(() => {
    observer.value?.disconnect?.();
});

onMounted(() => {
    workOrderStore.reloadArchived();

    observer.value = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    loadMoreItems();
                }
            });
        },
        {
            // The records table scrolls inside this container, so the observer root must be the container.
            root: scrollContainer.value,
            threshold: 0.1,
            rootMargin: '200px',
        },
    );

    if (landmark.value) {
        observer.value.observe(landmark.value);
    }
});

const loadMoreItems = () => {
    workOrderStore.loadMoreArchived();
};
</script>
